import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useSiteService } from './siteService';
import { SiteCard } from './SiteCard';
import { SearchableList } from '../shared/SearchableList';
import { sitesStatusFilterAtom, sitesTypeFilterAtom } from '../shared/filterState';
import { SiteLifecycleStatus, toSiteLifecycleStatus, siteLifecycleStatusToNumber } from '../tenants/tenantService';
import {
  Box, FormControl, InputLabel, Select, MenuItem, OutlinedInput, SelectChangeEvent,
  Checkbox, ListItemText, Button, Paper, Typography, FormControlLabel
} from '@mui/material';

export const SitesList = () => {
  const siteService = useSiteService();

  const [sites, setSites] = useState<any>(null);
  const [selectedStatuses, setSelectedStatuses] = useRecoilState(sitesStatusFilterAtom);
  const [selectedSiteTypes, setSelectedSiteTypes] = useRecoilState(sitesTypeFilterAtom);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<SiteLifecycleStatus>(SiteLifecycleStatus.Active);
  const [updating, setUpdating] = useState(false);

  const loadSites = () => {
    const needDeleted = selectedStatuses.includes(SiteLifecycleStatus.Deleted);
    siteService.getAll(needDeleted).then(setSites);
  };

  useEffect(() => {
    loadSites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses]);

  const allStatuses = Object.values(SiteLifecycleStatus);
  const allStatusesSelected = selectedStatuses.length === allStatuses.length;

  // Extract unique site types from loaded sites
  const allSiteTypes: string[] = sites
    ? Array.from(new Set(sites.map((s: any) => s.siteType).filter(Boolean) as string[])).sort()
    : [];
  const allSiteTypesSelected = selectedSiteTypes.length === allSiteTypes.length;

  const handleStatusChange = (event: SelectChangeEvent<SiteLifecycleStatus[]>) => {
    const value = event.target.value;
    if (value.includes('__all__' as any)) {
      setSelectedStatuses(allStatusesSelected ? [] : allStatuses);
    } else {
      setSelectedStatuses(typeof value === 'string' ? value.split(',') as SiteLifecycleStatus[] : value);
    }
  };

  const handleSiteTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    if (value.includes('__all__')) {
      setSelectedSiteTypes(allSiteTypesSelected ? [] : allSiteTypes);
    } else {
      setSelectedSiteTypes(typeof value === 'string' ? value.split(',') : value);
    }
  };

  const filteredSites = sites?.filter((s: any) => {
    // Filter by lifecycle status
    if (selectedStatuses.length > 0) {
      const status = toSiteLifecycleStatus(s.lifecycleStatus);
      if (!selectedStatuses.includes(status)) return false;
    }
    // Filter by site type
    if (selectedSiteTypes.length > 0) {
      if (!selectedSiteTypes.includes(s.siteType)) return false;
    }
    return true;
  });

  const handleSelectChange = (id: string, selected: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredSites?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSites?.map((s: any) => s.id) || []));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;

    setUpdating(true);
    try {
      const updatePromises = Array.from(selectedIds).map(async (id) => {
        const site = sites.find((s: any) => s.id === id);
        if (site) {
          await siteService.put(id, { ...site, lifecycleStatus: siteLifecycleStatusToNumber(bulkStatus) });
        }
      });
      await Promise.all(updatePromises);
      setSelectedIds(new Set());
      loadSites();
    } catch (error) {
      console.error('Failed to update sites:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 2, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Lifecycle Status</InputLabel>
          <Select
            multiple
            value={selectedStatuses}
            onChange={handleStatusChange}
            input={<OutlinedInput label="Lifecycle Status" />}
            renderValue={(selected) => allStatusesSelected ? 'Alle' : selected.join(', ')}
          >
            <MenuItem value={'__all__' as any}>
              <Checkbox checked={allStatusesSelected} indeterminate={selectedStatuses.length > 0 && !allStatusesSelected} />
              <ListItemText primary="Velg alle" />
            </MenuItem>
            {allStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={selectedStatuses.includes(status)} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Site Type</InputLabel>
          <Select
            multiple
            value={selectedSiteTypes}
            onChange={handleSiteTypeChange}
            input={<OutlinedInput label="Site Type" />}
            renderValue={(selected) => allSiteTypesSelected ? 'Alle' : selected.join(', ')}
          >
            <MenuItem value={'__all__'}>
              <Checkbox checked={allSiteTypesSelected} indeterminate={selectedSiteTypes.length > 0 && !allSiteTypesSelected} />
              <ListItemText primary="Velg alle" />
            </MenuItem>
            {allSiteTypes.map((siteType) => (
              <MenuItem key={siteType} value={siteType}>
                <Checkbox checked={selectedSiteTypes.includes(siteType)} />
                <ListItemText primary={siteType} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedIds.size > 0 && (
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedIds.size === filteredSites?.length && filteredSites?.length > 0}
                indeterminate={selectedIds.size > 0 && selectedIds.size < filteredSites?.length}
                onChange={handleSelectAll}
              />
            }
            label={`Velg alle synlige (${filteredSites?.length || 0})`}
          />
        </Box>
      )}

      {selectedIds.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography>
              {selectedIds.size} site{selectedIds.size > 1 ? 's' : ''} valgt
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}>
              <Select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value as SiteLifecycleStatus)}
              >
                {allStatuses.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBulkUpdate}
              disabled={updating}
            >
              {updating ? 'Oppdaterer...' : 'Sett status'}
            </Button>
            <Button
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
              onClick={() => setSelectedIds(new Set())}
            >
              Avbryt
            </Button>
          </Box>
        </Paper>
      )}

      <SearchableList
        pageKey="sites"
        list={filteredSites}
        renderElement={(s: any) => (
          <SiteCard
            key={s.id}
            site={s}
            selectable={true}
            isSelected={selectedIds.has(s.id)}
            onSelectChange={handleSelectChange}
          />
        )}
      />
    </>
  );
}
